import { Avatar, Box, Grid, Stack, useTheme } from "@mui/material";
import { AppGlobalClassKeys } from "app/appGlobalStyles";
import { MprcLink, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { FeedContentLoadStatus, PublicationType } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import { useModalUserProfile } from "providers/UserProfileProvider";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { resolveAuthor } from "views/knowledge/components/card/behaviors/common/DefaultAuthor";
import { AnswerOptions, QuestionOptions } from "views/knowledge/components/card/behaviors/common/DefaultOptions";
import { CommentButton, FavoriteButton, LikeButton, ShareButton } from "views/knowledge/components/card/behaviors/common/Interaction";
import { StickerBox } from "views/knowledge/components/card/behaviors/common/StickerBox";
import { useCardContext } from "views/knowledge/components/card/CardBuilder";
import { CardContextLink } from "views/knowledge/components/card/CardContextLink";
import { ModeratorMessageButton } from "../../actions/ModeratorMessageDialog";

///
export const DefaultTitle = () => {
    const { pub, pub: { answerDefinition } } = useCardContext();
    const theme = useTheme();
    const { t } = useTranslation("knowledge");

    const isBlocked = pub.blocked?.blockedOn != undefined;

    var borderStyle: React.CSSProperties | undefined = (pub.interaction.isNewPublication || pub.blocked?.firstAccessAfterBlock === true || pub.rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    return (
        <div style={borderStyle}>
            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {answerDefinition?.isBestAnswer && <StickerBox name={t("Feed.Card.Answer.BestAnswerText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.success.main} />}
                {isBlocked && <StickerBox name={t("Feed.Card.Common.BlockedText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.warning.main} />}
            </Stack>
        </div>
    );
};

///
export const QuestionTitle = () => {
    const { pub, pub: { answerDefinition } } = useCardContext();

    const theme = useTheme();
    const { t } = useTranslation("knowledge");

    var borderStyle: React.CSSProperties | undefined = (pub.interaction.isNewPublication || pub.blocked?.firstAccessAfterBlock === true || pub.rejected?.firstAccessAfterRejected === true)
        ? { borderLeft: "4px solid", borderLeftColor: theme.palette.warning.main, paddingLeft: theme.spacing(1) }
        : undefined;

    var stickerDetail = answerDefinition!.questionHasDetail ? t("Feed.Card.Question.MoreDetailText") : "";
    stickerDetail += answerDefinition!.answerCount > 1
        ? `${answerDefinition!.questionHasDetail ? ' - ' : ''}${t("Feed.Card.Question.StickerAnswersCountText", { count: answerDefinition!.answerCount })}`
        : "";

    return (
        <div style={borderStyle}>
            {answerDefinition!.questionIsAvailable === true ?
                <CardContextLink
                    publicationId={pub.publicationId}
                    to={KnowledgePaths.PublicationViewerResolver(answerDefinition!.questionId, PublicationType.Question)}
                >
                    <Box sx={{ color: theme.palette.common.black }}>
                        <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                            <span title={pub.title}>{pub.title}</span>
                        </MprcFeedTypography>
                    </Box>
                </CardContextLink>
                :
                <Box sx={{ color: theme.palette.common.black }}>
                    <MprcFeedTypography variant="title" component="div" noWrap={true} color={"inherit"}>
                        <span title={pub.title}>{pub.title}</span>
                    </MprcFeedTypography>
                </Box>
            }
            <Stack spacing={0.5} alignItems="flex-end" direction="row">
                {answerDefinition!.questionIsAvailable === false && <StickerBox name={t("Feed.Card.Answer.QuestionUnavaliableText")} textColor={theme.palette.common.white} backgroundColor={theme.palette.error.main} variant="stickerLowercase" />}
                {stickerDetail != "" && <StickerBox name={stickerDetail} textColor={theme.palette.common.white} backgroundColor={theme.palette.primary.light} />}
            </Stack>
        </div>
    );
};

///
export const AnswerAuthor = () => {
    const { pub } = useCardContext();
    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();
    const location = useLocation();
    const { showUser } = useModalUserProfile();

    const session = useSession();

    var author = resolveAuthor(pub, t, session.user.userId);

    const RenderAuthorName = (
        <MprcFeedTypography variant="authorPrimary" component="span" noWrap>
            <span title={author.name}>{author.name}</span>
            {
                pub.supervisor != null &&
                <span> ({t("Feed.Card.Common.EditedBySupervisorText")})</span>
            }
        </MprcFeedTypography>
    );

    return (
        <>
            <Grid container justifyContent="center" alignItems="center" spacing={1} width="100%">
                {!pub.isAnonymous &&
                    <Grid>
                        {pub.institutional != null
                            ? <Avatar src={author.thumbnailUrl} title={author.name} />
                            : <Avatar
                                className={AppGlobalClassKeys.clickable}
                                title={author.name}
                                src={author.thumbnailUrl}
                                onClick={
                                    () => showUser(
                                        pub.author.userId,
                                        () => navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId: pub.publicationId } })
                                    )
                                }
                            />
                        }
                    </Grid>
                }
                <Grid size="grow" overflow="hidden">
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        {
                            (pub.institutional != null || pub.isAnonymous)
                                ? RenderAuthorName
                                : (
                                    <MprcLink
                                        onClick={
                                            () => showUser(
                                                pub.author.userId,
                                                () => navigate(document.location.search, { replace: true, state: { ...(location.state ?? {}), publicationId: pub.publicationId } })
                                            )
                                        }
                                    >
                                        {RenderAuthorName}
                                    </MprcLink>
                                )
                        }
                    </Stack>
                    <MprcFeedTypography variant="authorSecondary" component="div" noWrap>
                        <span>{t("Feed.Card.Common.AnsweredhOnText")} {Formaters.relativeTime(author.actionDate)}</span>
                    </MprcFeedTypography>
                </Grid>
            </Grid>
        </>
    );
};


///
export const DefaultContent = () => {
    const {
        pub: { publicationId, content },
        localContent,
        onUpdateLocalContent,
    } = useCardContext();

    const api = useCoreApi();
    const { t } = useTranslation("knowledge");

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<any>(undefined);

    const loadContent = () => {
        setLoading(true);
        api.v1.knowledgePublication.getContent({ publicationId: publicationId })
            .then((result) => {
                onUpdateLocalContent({
                    content: result.data,
                    contentLoadStatus: FeedContentLoadStatus.AllContentLoaded,
                    state: result.data != null ? "expanded" : undefined
                });

            })
            .catch((e) => setError(e))
            .finally(() => setLoading(false));
    };

    const toogleState = () => onUpdateLocalContent({ ...localContent, state: localContent.state == "expanded" ? "contracted" : "expanded" });

    return error
        ? <MprcRequestErrorReport error={error} />
        : (
            <section id="k-content">
                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.AllContentLoaded &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: localContent.state == "expanded" ? localContent.content! : content! }}></div>
                        </MprcFeedTypography>
                        <MprcFeedTypography variant="moreContent" component="a" onClick={toogleState}>
                            {localContent.state == "expanded" ? t("Feed.Card.Common.SeeLessText") : t("Feed.Card.Common.SeeMoreText")}
                        </MprcFeedTypography>
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.LoadContentInDemand &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                        {
                            loading
                                ? <MprcSpinnerIndicator />
                                : <MprcFeedTypography variant="moreContent" component="a" onClick={() => loadContent()}>
                                    {t("Feed.Card.Common.SeeMoreText")}
                                </MprcFeedTypography>
                        }
                    </>
                }

                {
                    localContent.contentLoadStatus == FeedContentLoadStatus.NoActionNecessary &&
                    <>
                        <MprcFeedTypography variant="content" component="div" className="ql-editor">
                            <div dangerouslySetInnerHTML={{ __html: content ?? "" }}></div>
                        </MprcFeedTypography>
                    </>
                }
            </section>
        );
};

///
export const DefaultFooter = () => {

    const { pub: { blocked } } = useCardContext();

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid>
                        <LikeButton />
                    </Grid>
                    <Grid>
                        <CommentButton />
                    </Grid>
                </Grid>
            </Grid>
            {
                blocked?.blockedOn != null &&
                <Grid>
                    <ModeratorMessageButton optionMode="blocked" />
                </Grid>
            }
            <Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                        <FavoriteButton />
                    </Grid>
                    <Grid>
                        <ShareButton />
                    </Grid>
                    <Grid>
                        <AnswerOptions />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};

///
export const QuestionFooter = () => {

    const { pub: { blocked } } = useCardContext();

    return (
        <Grid container justifyContent="space-between" alignItems="center">
            <Grid>
                <Grid container spacing={2} justifyContent="center" alignItems="center">
                    <Grid>
                        <LikeButton />
                    </Grid>
                    <Grid>
                        <CommentButton />
                    </Grid>
                </Grid>
            </Grid>
            {
                blocked?.blockedOn != null &&
                <Grid>
                    <ModeratorMessageButton optionMode="blocked" />
                </Grid>
            }
            <Grid>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid>
                        <FavoriteButton />
                    </Grid>
                    <Grid>
                        <ShareButton />
                    </Grid>
                    <Grid>
                        <QuestionOptions />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
};
